export const getMetafield = (product: any, namespace: string, key: string) =>
  product?.metafields?.find((m: any) => m?.namespace === namespace && m?.key === key)?.value

export const getHandle = (product: any): string | undefined => product?.handle || product?.shopify?.handle

// Sanity sibling lists keep weak references to products that have since been archived, unpublished
// or deleted, and include the product itself. Keep only the published ones, matching the PDP selector.
export const getPublishedSiblings = (product: any): any[] =>
  (product?.siblings || []).filter((sibling: any) => sibling?.shopify?.active && sibling?.shopify?.published && !sibling?.shopify?.deleted)

// Sanity products (and live products built from them) carry `siblings` or `shopify`. Their `related`
// is the "You may also like" list, so only Reactify hits use `related` as the scent group.
// Checked by value, not key: useLiveProducts always writes `siblings`, even when undefined.
const isSanityProduct = (product: any): boolean => !!product && (product?.siblings != null || !!product?.shopify)

// Handles of the product's other published options, deduped and excluding the product itself.
export const getSiblingHandles = (product: any): string[] => {
  const handle = getHandle(product)
  const handles = getPublishedSiblings(product).map(getHandle).filter(Boolean) as string[]
  return [...new Set(handles)].filter(sibling => sibling !== handle)
}

// Reactify hits expose their scent group as Storefront-shaped `related` products instead.
export const getRelatedGroup = (product: any): any[] => (isSanityProduct(product) ? [] : product?.related || [])

// Number of options a product card offers: itself plus its published sibling group (Sanity `siblings`,
// or Reactify `related`). Counted by unique handle so the product isn't counted twice when it's
// listed in its own group.
export const getOptionCount = (product: any): number => {
  const group = isSanityProduct(product) ? getSiblingHandles(product) : getRelatedGroup(product).map(getHandle)
  const handles = new Set([getHandle(product), ...group].filter(Boolean))
  return Math.max(handles.size, 1)
}
